import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import { courseToSteer, type CtsResult } from "@nav-games/chartwork";
import { formatHeading, normalizeDeg } from "@nav-games/compass";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface CtsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "chartwork";
const GAME = "cts";
const CARDS_PER_SESSION = 8;
const CHOICES_PER_CARD = 4;

interface Card {
  groundTrack: number;
  boatSpeed: number;
  set: number;
  rate: number;
  result: CtsResult;
  target: number;
  choices: readonly string[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function buildCard(): Card {
  // Regenerate until we get a feasible problem with a non-trivial
  // tidal offset (so the answer isn't just the ground track).
  for (let attempt = 0; attempt < 50; attempt++) {
    const groundTrack = randInt(0, 359);
    const boatSpeed = randInt(4, 7);
    const set = normalizeDeg(groundTrack + randInt(30, 330));
    const rate = randInt(1, 3);
    const result = courseToSteer({
      groundTrackDeg: groundTrack,
      boatSpeedKts: boatSpeed,
      setDeg: set,
      rateKts: rate,
    });
    if (!result.feasible || Math.abs(result.offsetDeg) < 3) continue;

    const target = Math.round(result.courseToSteerDeg);
    const pool = [
      Math.round(groundTrack), // forgot the tide
      Math.round(normalizeDeg(groundTrack - result.offsetDeg)), // wrong way
      Math.round(normalizeDeg(result.courseToSteerDeg + 2 * result.offsetDeg)),
    ];
    const chosen: number[] = [];
    const seen = new Set<number>([normalizeDeg(target)]);
    for (const cand of pool) {
      const n = normalizeDeg(cand);
      if (chosen.length >= CHOICES_PER_CARD - 1) break;
      if (!seen.has(n)) {
        seen.add(n);
        chosen.push(n);
      }
    }
    let off = 6;
    while (chosen.length < CHOICES_PER_CARD - 1) {
      for (const cand of [normalizeDeg(target + off), normalizeDeg(target - off)]) {
        if (chosen.length >= CHOICES_PER_CARD - 1) break;
        if (!seen.has(cand)) {
          seen.add(cand);
          chosen.push(cand);
        }
      }
      off += 4;
    }
    const all = [normalizeDeg(target), ...chosen].map((n) => `${formatHeading(n)}°`);
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const ai = all[i]!;
      all[i] = all[j]!;
      all[j] = ai;
    }
    return { groundTrack, boatSpeed, set, rate, result, target: normalizeDeg(target), choices: all };
  }
  // Fallback (essentially never hit): a known feasible problem.
  const result = courseToSteer({ groundTrackDeg: 90, boatSpeedKts: 5, setDeg: 180, rateKts: 2 });
  const target = normalizeDeg(Math.round(result.courseToSteerDeg));
  return {
    groundTrack: 90,
    boatSpeed: 5,
    set: 180,
    rate: 2,
    result,
    target,
    choices: [`${formatHeading(target)}°`, "090°", "100°", "080°"],
  };
}

export function Cts({ onNavigate }: CtsProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === `${formatHeading(card.target)}°`;
      const attempt: Omit<Attempt, "module"> = {
        char: "cts",
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/chartwork")}
      />
    );
  }

  const revealed = chosen !== null;
  const correctStr = `${formatHeading(card.target)}°`;
  const side =
    card.result.offsetDeg < 0 ? "to port" : "to starboard";

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "chartwork" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Course to steer</h3>
        <p style={{ marginBottom: 12 }}>
          Make good a ground track of <strong>{formatHeading(card.groundTrack)}°T</strong>.
          Boat speed <strong>{card.boatSpeed} kt</strong>. The tide sets{" "}
          <strong>{formatHeading(card.set)}°T</strong> at{" "}
          <strong>{card.rate} kt</strong>.
          <br />
          What course do you steer (°T)?
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === correctStr;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 20,
                  padding: "14px 0",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: 12,
              borderRadius: 6,
              background:
                chosen === correctStr
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === correctStr
                ? `✓ Correct — steer ${correctStr}T.`
                : `✗ Not quite — steer ${correctStr}T.`}
            </p>
            <p className="small muted" style={{ margin: "8px 0 0" }}>
              The tide pushes {Math.abs(card.result.tideAcrossKts).toFixed(1)} kt
              across the track, so steer{" "}
              {Math.abs(Math.round(card.result.offsetDeg))}° {side} of it. Speed
              over the ground works out at{" "}
              <strong>{card.result.speedOverGroundKts.toFixed(1)} kt</strong>.
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next question →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/chartwork")}>
        ← Back to Chartwork
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Chartwork
        </button>
      </div>
    </>
  );
}
